import * as z from "zod";

// NOTE (Yuxin, 2026-07-22): the canary registry lives in CODE, not Neon. This
// file is the single source of truth for which canaries exist and how they're
// scheduled + presented. An agent edits this array and ships it in a deploy —
// there is no DB table or UI to hand-edit. Neon stores only run history
// (`canary_run`), keyed by `name`, which points back into this config.
//
// Scheduling, identity, and the production resources each canary touches live
// here; per-canary runtime behavior (browser/ai/blockIframes) stays in the
// spec's defineCanary() call, joined by `name`. `name` must equal
// tests/<name>.canary.ts and the spec's name.
//
// Presentation order is hardcoded, not health-derived: categories render in
// CANARY_CATEGORIES order, and within a category canaries keep their order in
// this array. `sortedCanaryConfigs` imposes that ordering.

export const canaryMachineSchema = z.enum([
  "micro",
  "small-1x",
  "small-2x",
  "medium-1x",
  "medium-2x",
  "large-1x",
  "large-2x",
]);
export type CanaryMachine = z.infer<typeof canaryMachineSchema>;

// NOTE (Yuxin, 2026-07-27): a canary's category groups the /agent-status tabs
// and the Slack board into product-surface sections. Display names live in
// CANARY_CATEGORY_NAMES, hardcoded section order in CANARY_CATEGORY_ORDER.
export const canaryCategorySchema = z.enum([
  "e2e",
  "miniapps",
  "shopify-theme-builder",
]);
export type CanaryCategory = z.infer<typeof canaryCategorySchema>;

// A Record (not a find-with-fallback) so adding a category is a compile error
// until it has a display name. Shown identically in the UI and every Slack message.
export const CANARY_CATEGORY_NAMES: Record<CanaryCategory, string> = {
  e2e: "E2E",
  miniapps: "Features",
  "shopify-theme-builder": "Shopify Theme Builder",
};

// The hardcoded render order for UI tabs and Slack board sections.
const CANARY_CATEGORY_ORDER = [
  "e2e",
  "miniapps",
  "shopify-theme-builder",
] as const satisfies readonly CanaryCategory[];

export const CANARY_CATEGORIES: readonly {
  slug: CanaryCategory;
  name: string;
}[] = CANARY_CATEGORY_ORDER.map((slug) => ({
  slug,
  name: CANARY_CATEGORY_NAMES[slug],
}));

/** The display label for a category, shared by the UI and the Slack board. */
export function canaryCategoryName(category: CanaryCategory): string {
  return CANARY_CATEGORY_NAMES[category];
}

/** A category's rank in the hardcoded section order. */
export function canaryCategoryOrder(category: CanaryCategory): number {
  return CANARY_CATEGORY_ORDER.indexOf(category);
}

export const canaryTypeSchema = z
  .string()
  .min(1)
  .max(256)
  .regex(/^[a-z0-9-]+$/);

/**
 * Stamped on eval-runner projects so alerts and analytics that skip canary
 * types ignore them. A canary type alone does not make a project disposable:
 * eval projects are never classified `canary`, and the canary cleanup sweep
 * deletes only projects with that classification.
 */
export const EVAL_PROJECT_CANARY_TYPE = "eval";

/** The standing account browser canaries log in as and API canaries mint keys from. */
export const CANARY_ACCOUNT_EMAIL = "canaries@replo.app";

export const canaryConfigSchema = z.object({
  /** Machine id: equals tests/<slug>.canary.ts and every canary_run.canary. */
  slug: canaryTypeSchema,
  /** Human display name — shown identically in the UI and the Slack messages. */
  name: z.string().min(1),
  /** Product surface this canary probes; groups the UI + Slack board sections. */
  category: canaryCategorySchema,
  /** One-sentence summary shown under the name. */
  description: z.string().min(1),
  /** The concrete things this canary asserts — rendered as a checkmark list on hover. */
  verifies: z.array(z.string().min(1)).min(1),
  /**
   * The standing account this canary acts as; null if it signs up a throwaway
   * (buildCanarySignupEmail derives that address from `slug`). Required,
   * because "who does this run as" is what makes a project link open the
   * canary's own actor-scoped sandbox instead of the viewer's.
   */
  account: z.email().nullable(),
  /** Signup canaries mint one per run; drives its admin pill and in-run teardown. */
  createsWorkspacePerRun: z.boolean().default(false),
  /** Each project it drives: a standing project's id, or null if it makes one per run. */
  projects: z.array(z.uuid().nullable()),
  /** Cron expression the dispatcher fires on (UTC). */
  cron: z.string().min(1),
  /**
   * Trigger machine preset. Browser canaries default to medium-1x; if one
   * outgrows medium's memory it just goes red (no auto-retry) so we notice and
   * bump it here. HTTP-only canaries run small-1x; brand-studio pins large-1x
   * pending a memory profile before downsizing.
   */
  machine: canaryMachineSchema.default("medium-1x"),
  /** Hard cap for a single run, milliseconds. */
  timeoutMs: z.number().int().positive().default(1_500_000),
  /** Set false to stop scheduling it (takes effect on the next deploy). */
  enabled: z.boolean().default(true),
  /** Optional Slack handle for escalation. */
  owner: z.string().min(1).nullish(),
});
export type CanaryConfig = z.infer<typeof canaryConfigSchema>;

function findCanaryType(email: string): string | null {
  const [localPart = "", domain, extra] = email.toLowerCase().split("@");
  if (domain !== "replo.app" || extra) {
    return null;
  }
  // "canary" is the current marker segment; "test" is the legacy one, accepted
  // forever because historical rows keep their signup email.
  const [account, marker, canaryType] = localPart.split("+");
  if (account !== "canaries" || (marker !== "canary" && marker !== "test")) {
    return null;
  }
  const result = canaryTypeSchema.safeParse(canaryType);
  return result.success ? result.data : null;
}

export function getCanaryIdentity({
  email,
  canaryType,
}: {
  email: string;
  canaryType?: string;
}): { isCanary: boolean; canaryType: string | null } {
  if (email.toLowerCase() === CANARY_ACCOUNT_EMAIL) {
    return { isCanary: true, canaryType: canaryType ?? null };
  }
  const emailCanaryType = findCanaryType(email);
  if (!emailCanaryType) {
    return { isCanary: false, canaryType: null };
  }
  return {
    isCanary: true,
    canaryType: canaryType ?? emailCanaryType,
  };
}

// Fixture project ids are duplicated from ./canaryFixtures.mts on purpose:
// this registry is bundled into replo-admin, whose bundler cannot resolve the
// `.mjs` specifier TypeScript uses for that `.mts` file. canaryConfig.unit.spec.ts
// fails if any id here is missing from PROTECTED_CANARY_PROJECT_IDS.
const REGISTRY_WEBSITE_CANARY_PROJECT_ID =
  "923cc978-219b-4e78-b42d-32339644e0d3";
const PUBLISH_ONLY_CANARY_PROJECT_ID = "e3bb44f7-7e7c-4af8-b259-82a18eca68d1";
const SCHEDULED_TASK_CANARY_PROJECT_ID = "2eb9126e-8c98-4095-9571-a9e2384b9f90";
const EXISTING_SITE_CANARY_PROJECT_ID = "ad5719c2-95db-46ff-845d-3198fef173f4";
const PUBLISHING_API_CANARY_PROJECT_ID = "8e1e1d14-7b5c-432d-a370-670bc70fdb01";
const BRAND_STUDIO_CANARY_PROJECT_ID = "d2cc83e0-dbb2-41b6-9d88-3345bb8c3412";
const FILES_CANARY_PROJECT_ID = "92606040-8d56-461c-9ea1-f8376a6292ae";
const ASSETS_CANARY_PROJECT_ID = "4db218ff-4f7b-4a98-abed-b1d83545361b";
const SKILLS_CANARY_PROJECT_ID = "e77c1ef5-e70f-41e8-921b-ac8672feb1cb";

// Crons use staggered minutes so sandbox boots and LLM traffic do not spike together.
//
// Typed as the schema's INPUT (defaults still optional) so a row missing a
// required field fails `tsc`, not module load in production.
const RAW_CANARY_CONFIGS: z.input<typeof canaryConfigSchema>[] = [
  {
    slug: "new-project-lifecycle",
    name: "New Project Lifecycle",
    category: "e2e",
    description:
      "Creates a project and site, edits it, publishes, and verifies the live URL.",
    verifies: [
      "Project creates in the Canaries workspace",
      "Project creation provisions the initial site",
      "Sandbox boots and the builder loads",
      "Fresh subdomain is not live pre-publish",
      "Agent edit renders in the builder preview",
      "Publish completes in the builder UI",
      "Published URL serves the edited content",
      "Site and project delete cleanly",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [null],
    cron: "15 * * * *",
    // Chromium, the live preview, and the screencast share one 2GB cgroup. A
    // long publish-pill wait OOM-kills the task before it can post a result.
    machine: "medium-2x",
  },
  {
    slug: "existing-site-lifecycle",
    name: "Existing Site Lifecycle",
    category: "e2e",
    description:
      "Edits the standing fixture site through the builder, publishes, and verifies the live URL.",
    verifies: [
      "Standing site resolves (self-seeds once) and opens in the builder",
      "Agent edit renders in the builder preview",
      "Publish completes via the builder UI",
      "Live URL serves this run's edited content",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [EXISTING_SITE_CANARY_PROJECT_ID],
    cron: "30 * * * *",
    // Stagehand + a live agent turn + publish starve the renderer on 1 vCPU:
    // 2x wall-clock and click-timeout reds on medium-1x (2026-08-20/21).
    machine: "large-1x",
  },
  {
    slug: "create-publish-delete-site-api",
    name: "Site Builder API Lifecycle",
    category: "e2e",
    description:
      "Creates, publishes, and confirms a site goes live via the public API.",
    verifies: [
      "Site creates via the public API",
      "New site is not live yet",
      "Publish succeeds",
      "Live URL serves the published page",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [PUBLISHING_API_CANARY_PROJECT_ID],
    cron: "0 * * * *",
    machine: "small-1x",
  },
  {
    slug: "publish-only",
    name: "Publishing Existing Sites",
    category: "e2e",
    description:
      "Opens and republishes the hardcoded fixture site and checks the UI reports success.",
    verifies: [
      "Hardcoded fixture site canvas loads",
      "Publish confirms",
      "UI reports publish success",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [PUBLISH_ONLY_CANARY_PROJECT_ID],
    cron: "13 * * * *",
    // 2GB OOMs while Chromium waits on the publish pill, after the site is
    // already live, so the task dies before it can post a result.
    machine: "medium-2x",
  },
  {
    slug: "agent-onboarding",
    name: "Onboarding",
    category: "e2e",
    description:
      "Signs up through the Agent funnel and lands on the new project's Home tab.",
    verifies: [
      "Agent-funnel signup completes",
      "Onboarding questions submit",
      "Project is provisioned",
      "Free workspaces see and can skip the plan chooser",
      "Home tab opens active with the greeting",
    ],
    account: null,
    createsWorkspacePerRun: true,
    projects: [null],
    cron: "45 * * * *",
    machine: "medium-1x",
  },
  {
    slug: "harness-skills-check",
    name: "Agent Skills",
    category: "miniapps",
    description: "Confirms the sandbox agent has exactly the skills we ship.",
    verifies: [
      "Fresh sandbox uses a production snapshot",
      "Harness manifest matches every installed file",
      "Sandbox converged to its recorded harness version",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [null],
    cron: "0 * * * *",
    machine: "medium-1x",
  },
  {
    slug: "skill-sharing-flow",
    name: "Skill Sharing",
    category: "miniapps",
    description:
      "Shares a skill from one project and installs it into another.",
    verifies: [
      "Skill seeds into a sandbox",
      "Skill shares to the registry",
      "Skill installs into a second project without a site picker",
      "Content survives the round-trip",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [null, null],
    cron: "45 * * * *",
    machine: "medium-1x",
  },
  {
    slug: "page-sharing-flow",
    name: "Page Sharing",
    enabled: false,
    category: "miniapps",
    description:
      "Checks anonymous live previews and registry template sharing in the desktop editor.",
    verifies: [
      "Site installs from a template",
      "Automatically prepared live preview copies and renders the same page anonymously",
      "Public preview responses discourage indexing with noindex and nofollow",
      "Share UI switches from Live preview to Template",
      "Homepage shares to the registry",
      "Shared page installs into the chosen site (collision-safe copy)",
      "Installed preview renders content",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [REGISTRY_WEBSITE_CANARY_PROJECT_ID],
    cron: "30 * * * *",
    machine: "medium-1x",
  },
  {
    slug: "registry-read-flow",
    name: "Registry Read API",
    category: "miniapps",
    description: "Walks the public registry read endpoints and their auth.",
    verifies: [
      "Anonymous reads are rejected (401)",
      "Browser API token mints",
      "Registry item list, preview, detail & content load",
      "Categories & tags load; one- and two-tag filters succeed",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [],
    cron: "15 * * * *",
    machine: "medium-1x",
  },
  {
    slug: "scheduled-task-health-check",
    name: "Scheduled Tasks",
    category: "miniapps",
    description: "Checks a watched scheduled task's latest run is healthy.",
    verifies: [
      "Latest task run is found",
      "The run didn't fail",
      "The run isn't stuck/hung",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [SCHEDULED_TASK_CANARY_PROJECT_ID],
    cron: "30 * * * *",
    machine: "small-1x",
  },
  {
    slug: "brand-studio-lifecycle",
    name: "Branding Lifecycle",
    category: "miniapps",
    description:
      "Starts a blank brand in Branding, edits it, writes its business guidelines, and confirms the API and BRAND.json agree.",
    verifies: [
      "Deleting the brand through the API returns the project to its setup workflows",
      "Naming the brand in Build it from scratch creates the brand rows",
      "Edit details saves the business facts",
      "A color added in the editor saves with its hex",
      "GET /context carries the edited name and hex",
      "BRAND.json is rendered from the rows at the same version, with no legacy files beside it",
      "Business guidelines typed in Brand details save and Branding shows them",
      "BRAND.json carries the business overview",
      "The brand is reset after every run",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [BRAND_STUDIO_CANARY_PROJECT_ID],
    cron: "1 * * * *",
    machine: "large-1x",
    owner: "yuxin",
  },
  {
    slug: "brand-import",
    name: "Brand Website Import",
    category: "miniapps",
    description:
      "Imports a controlled website through Branding and verifies the extracted brand rows.",
    verifies: [
      "The website import submits bypassCache to run both model extractions afresh",
      "The saved business overview is non-empty",
      "The saved palette contains at least one hex color",
      "The saved fonts include a family from the page",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [null],
    // Nightly (09:45 UTC, ~2:45am Pacific): each run pays for two fresh model
    // extractions, and post-deploy dispatch already runs it after every deploy.
    cron: "45 9 * * *",
    machine: "large-1x",
    enabled: true,
    owner: "yuxin",
  },
  {
    slug: "files-lifecycle",
    name: "Files Lifecycle",
    category: "miniapps",
    description:
      "Uploads, organizes, shares, downloads, and deletes an agency handoff.",
    verifies: [
      "Uploaded bytes appear in Files",
      "Folder creation, rename, and move work",
      "Project sharing persists",
      "Signed download serves exact bytes without auth",
      "UI deletion is confirmed server-side",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [FILES_CANARY_PROJECT_ID],
    cron: "4 * * * *",
    machine: "medium-1x",
    enabled: true,
    owner: "yuxin",
  },
  {
    slug: "assets-lifecycle",
    name: "Assets Lifecycle",
    category: "miniapps",
    description:
      "Uploads and moves three assets through draft, approved, and archived states.",
    verifies: [
      "Three distinct PNG assets upload",
      "API and UI status filters agree",
      "Approved CDN bytes are publicly cacheable",
      "Metadata edits are exactly filterable",
      "Deletion removes the asset from active results",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [ASSETS_CANARY_PROJECT_ID],
    cron: "7 * * * *",
    machine: "medium-1x",
    enabled: true,
    owner: "yuxin",
  },
  {
    slug: "skills-lifecycle",
    name: "Skills Lifecycle",
    category: "miniapps",
    description:
      "Authors a workspace skill and installs and removes a public registry skill.",
    verifies: [
      "Scratch editor writes an authored SKILL.md",
      "Authored skill appears and is searchable",
      "Owner affordances match authored ownership",
      "Registry install copies exact source bytes",
      "Uninstall and delete leave no sandbox ghosts",
    ],
    account: CANARY_ACCOUNT_EMAIL,
    projects: [SKILLS_CANARY_PROJECT_ID],
    // NOTE (Andrew, 2026-09-03): hourly for spend; cap must outlast a failing run's 3-min polls.
    cron: "26 * * * *",
    machine: "medium-1x",
    timeoutMs: 480_000,
    enabled: true,
    owner: "yuxin",
  },
  {
    slug: "onboarding",
    name: "V1 Onboarding",
    category: "shopify-theme-builder",
    description:
      "Signs up a new account through the Shopify theme builder funnel and lands in the editor.",
    verifies: [
      "Account signup completes",
      "Onboarding questionnaire submits",
      "Billing step is skippable",
      "Lands in the page editor",
    ],
    account: null,
    createsWorkspacePerRun: true,
    projects: [null],
    cron: "0 * * * *",
    machine: "medium-1x",
  },
];

// Validate + freeze at module load: the type above catches a missing field,
// this catches a malformed value (bad uuid, empty cron) — never at 3am mid-run.
export const CANARY_CONFIGS: readonly CanaryConfig[] = Object.freeze(
  z.array(canaryConfigSchema).parse(RAW_CANARY_CONFIGS),
);

export function findCanaryConfig(slug: string): CanaryConfig | undefined {
  return CANARY_CONFIGS.find((canary) => canary.slug === slug);
}

export function enabledCanaryConfigs(): readonly CanaryConfig[] {
  return CANARY_CONFIGS.filter((canary) => canary.enabled);
}

/**
 * Canaries in hardcoded presentation order: categories in CANARY_CATEGORIES
 * order, and within a category the order they appear in CANARY_CONFIGS. Stable
 * (index-tiebroken) so the sort never reshuffles same-category entries.
 */
export function sortedCanaryConfigs(
  configs: readonly CanaryConfig[] = CANARY_CONFIGS,
): CanaryConfig[] {
  return configs
    .map((canary, index) => ({ canary, index }))
    .sort((left, right) => {
      const byCategory =
        canaryCategoryOrder(left.canary.category) -
        canaryCategoryOrder(right.canary.category);
      return byCategory !== 0 ? byCategory : left.index - right.index;
    })
    .map((entry) => entry.canary);
}
